import React from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { useStore } from "@/contexts/StoreContext";
import { getProductPath } from "@/lib/api";
import { getMinimumCustomAmount, isCustomPricedProduct } from "@/lib/pricing";

const ProductCard = ({ product }) => {
    const { toggleWishlist, inWishlist } = useStore();
    const wished = inWishlist(product.id);
    const productPath = getProductPath(product);
    const isGeneratedKnitwear = product.id.startsWith("generated-knitwear-");
    const isVoucher = product.is_voucher === true;
    const isCustomPriced = isCustomPricedProduct(product);
    const startingPrice = getMinimumCustomAmount(product);
    const generatedKnitwearImageStyle = isGeneratedKnitwear
        ? { objectPosition: "center bottom" }
        : undefined;
    const primaryImageClass = isVoucher
        ? `absolute inset-0 block h-full w-full object-contain p-10 transition-all duration-500 ${
            product.images?.[1] ? "opacity-100 group-hover:opacity-0 group-hover:scale-[1.02]" : "group-hover:scale-[1.02]"
        }`
        : `absolute inset-0 block w-full h-full object-cover object-top transition-all duration-500 ${
            product.images?.[1] ? "opacity-100 group-hover:opacity-0 group-hover:scale-[1.02]" : "group-hover:scale-[1.02]"
        }`;
    const secondaryImageClass = isVoucher
        ? "absolute inset-0 block h-full w-full object-contain p-10 opacity-0 transition-all duration-500 group-hover:opacity-100 group-hover:scale-[1.02]"
        : "absolute inset-0 block w-full h-full object-cover object-top opacity-0 transition-all duration-500 group-hover:opacity-100 group-hover:scale-[1.02]";

    return (
        <div
            className="group relative flex flex-col border-2 border-black bg-ink"
            data-testid={`product-card-${product.id}`}
        >
            <Link
                to={productPath}
                className="relative block aspect-[4/5] overflow-hidden bg-white"
                data-testid={`product-card-link-${product.id}`}
            >
                <div className="absolute inset-y-0 inset-x-4 overflow-hidden bg-white">
                    <img
                        src={product.images?.[0]}
                        alt={product.name}
                        loading="lazy"
                        style={generatedKnitwearImageStyle}
                        className={primaryImageClass}
                    />
                    {product.images?.[1] && (
                        <img
                            src={product.images[1]}
                            alt={`${product.name} alternate view`}
                            loading="lazy"
                            style={generatedKnitwearImageStyle}
                            className={secondaryImageClass}
                        />
                    )}
                </div>
                {product.badge && (
                    <span
                        className="absolute top-3 left-3 bg-hazard text-black px-2 py-1 font-mono-ui text-[10px] tracking-[0.2em]"
                        data-testid={`product-badge-${product.id}`}
                    >
                        {product.badge}
                    </span>
                )}
                {product.images?.length > 1 && (
                    <span className="absolute bottom-3 left-3 border-2 border-black bg-white/90 px-2 py-1 font-mono-ui text-[10px] tracking-[0.2em] uppercase">
                        {product.images.length} views
                    </span>
                )}
                <span
                    className="absolute left-3 bottom-12 bg-hazard text-white px-2 py-1 font-mono-ui text-[10px] tracking-[0.2em] uppercase border-2 border-hazard"
                    data-testid={`product-source-${product.id}`}
                >
                    Handknitted
                </span>
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        toggleWishlist(product);
                    }}
                    className={`absolute top-3 right-3 flex h-9 w-9 items-center justify-center border-2 border-black transition-colors ${
                        wished ? "bg-hazard text-black" : "bg-ink hover:bg-ink hover:text-black"
                    }`}
                    aria-label="Add to wishlist"
                    data-testid={`product-wishlist-${product.id}`}
                >
                    <Heart className="w-4 h-4" fill={wished ? "currentColor" : "none"} />
                </button>
            </Link>

            <div className="flex items-baseline justify-between gap-2 border-t-2 border-dotted border-black p-4">
                <div className="min-w-0">
                    <Link
                        to={productPath}
                        className="block font-heading text-xl leading-none uppercase tracking-tight hover:text-hazard truncate"
                        data-testid={`product-name-${product.id}`}
                    >
                        {product.name}
                    </Link>
                    <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        {product.drop} / {product.category}
                    </p>
                    <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-hazard">
                        From Handknitted Images
                    </p>
                </div>
                <span
                    className="font-mono-ui text-sm whitespace-nowrap"
                    data-testid={`product-price-${product.id}`}
                >
                    {isCustomPriced ? `From $${startingPrice.toFixed(0)}` : `$${product.price.toFixed(0)}`}
                </span>
            </div>
        </div>
    );
};

export default ProductCard;
